import { SnapshotOptions } from 'firebase/firestore';
import {
  FirestoreDataConverter,
  GeoPoint,
  QueryDocumentSnapshot,
  Timestamp,
} from 'firebase/firestore';

export type GroupId = string;
export enum AttributeType {
  SINGLE_VALUE = 'value',
  LIST_SINGLE = 'list-single',
  LIST_SINGLE_FIXED = 'list-single-fixed',
  LIST_MULTIPLE = 'list-multiple',
  LIST_MULTIPLE_FIXED = 'list-multiple-fixed',
}

export type AttributeValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean | Timestamp | GeoPoint> // attribute type list-multiple
  | Timestamp
  | GeoPoint
  | undefined;

export enum AttributeValueType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  TIMESTAMP = 'timestamp',
  ARRAY = 'array',
  GEOPOINT = 'geopoint',
}

export interface DeckAttributeGroup {
  id: GroupId;
  label: string;
  order?: number;
  attributes?: DeckAttribute[];
}

/**
 * DeckAttribute
 * stored in multiple locations
 * 1. `/deck-attribute-groups/{groupId}` in the doc as attributes
 * 2. `/attributes/{attributeId}` in the doc as attribute with `/attributes/{attributeId}/options/{optionId}` as options
 * 3. `users/{userId}/decks/{deckId}` in the doc.attributeGroups[...].attributes as attributes
 */
export interface DeckAttribute {
  id?: string; // use id for type list to get options
  type: AttributeType;
  valueType: AttributeValueType;
  label: string;
  order?: number;
  value: AttributeValue;
  options?: DeckAttributeOption[] | undefined;
}

export interface DeckAttributeOption {
  id?: string;
  value: AttributeValue;
  type: AttributeValueType;
}

export const deckAttributeOptionConverter: FirestoreDataConverter<DeckAttributeOption> =
  {
    fromFirestore: (
      snapshot: QueryDocumentSnapshot,
      options?: SnapshotOptions
    ) => {
      const data = snapshot.data(options);
      return {
        id: snapshot.id,
        value: data['value'] || undefined,
        type: data['type'] || undefined,
      } as DeckAttributeOption;
    },
    toFirestore: (data: DeckAttributeOption) => {
      return {
        value: data.value || null,
        type: data.type || null,
      };
    },
  };

export const deckAttributeGroupConverter: FirestoreDataConverter<DeckAttributeGroup> & {
  fromObject: (p: unknown | undefined) => DeckAttributeGroup | undefined;
} = {
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ) => {
    const data = snapshot.data(options);
    return {
      id: snapshot.id,
      label: data['label'],
      order: data['order'],
      attributes: data['attributes']
        ? data['attributes'].map((attr: unknown) =>
            deckAttributeConverter.fromObject(attr)
          )
        : undefined,
    } as DeckAttributeGroup;
  },
  fromObject: (group: unknown | undefined) => {
    if (!group) {
      return undefined;
    }
    const g = group as Partial<DeckAttributeGroup>;
    return {
      id: g.id || undefined,
      label: g.label,
      order: g.order || 0,
      attributes: g.attributes
        ? g.attributes.map((attr: unknown) => {
            return deckAttributeConverter.fromObject(attr);
          })
        : undefined,
    } as DeckAttributeGroup;
  },
  toFirestore: (data: DeckAttributeGroup) => {
    return {
      id: data.id,
      label: data.label,
      attributes: data.attributes
        ? data.attributes.map((attribute) => {
            return deckAttributeConverter.toFirestore(attribute);
          })
        : null,
    };
  },
};

export const deckAttributeConverter: FirestoreDataConverter<DeckAttribute> & {
  fromObject: (obj: unknown | undefined) => DeckAttribute | undefined;
} = {
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ) => {
    const values = snapshot.data(options);
    const data: DeckAttribute = {
      id: snapshot.id,
      label: values['label'] || undefined,
      options: values['options'] || [],
      order: values['order'] || undefined,
      type: values['type'] || undefined,
      value: values['value'] || undefined,
      valueType: values['valueType'] || undefined,
    };
    return data;
  },

  fromObject: (data: unknown) => {
    if (!data) {
      return undefined;
    }
    const d = data as Partial<DeckAttribute>;
    return {
      id: d.id || undefined,
      type: d.type,
      valueType: d.valueType,
      label: d.label,
      order: d.order || undefined,
      value: d.value || undefined,
    } as DeckAttribute;
  },
  toFirestore: (data: DeckAttribute) => {
    return {
      id: data.id || null,
      type: data.type,
      valueType: data.valueType,
      label: data.label || null,
      order: data.order || null,
      value: data.value || null,
    };
  },
};
